import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, CalendarDays, MapPin, ChevronDown, Facebook, Twitter, Instagram, Camera, Award } from "lucide-react";
import { navLinks, conference } from "../mock";
import { openPhotoGallery, PHOTO_LINKS } from "../lib/utils";
import Logo from "./Logo";

const DesktopNavItem = ({ link }) => {
  const [open, setOpen] = useState(false);

  if (link.children) {
    return (
      <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
        <button className="px-3 py-2 rounded-lg text-sm font-medium text-navy-800 hover:text-brand hover:bg-orange-50 transition-colors inline-flex items-center gap-1 whitespace-nowrap">
          {link.label}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-xl border border-slate-100 py-2 min-w-[180px] z-50">
            {link.children.map((child) => (
              <NavLink
                key={child.to}
                to={child.to}
                className={({ isActive }) =>
                  `block px-4 py-2.5 text-sm font-medium transition-colors ${isActive ? "text-brand bg-orange-50" : "text-navy-800 hover:text-brand hover:bg-orange-50"}`
                }
              >
                {child.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={link.to}
      end={link.to === "/"}
      className={({ isActive }) =>
        `px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
          isActive ? "text-brand" : "text-navy-800 hover:text-brand hover:bg-orange-50"
        }`
      }
    >
      {link.label}
    </NavLink>
  );
};

const MobileNavItem = ({ link }) => {
  const [expanded, setExpanded] = useState(false);
  const location = useLocation();

  useEffect(() => setExpanded(false), [location.pathname]);

  if (link.children) {
    const isChildActive = link.children.some((c) => location.pathname === c.to);
    return (
      <div>
        <button
          onClick={() => setExpanded(!expanded)}
          className={`w-full px-4 py-3 rounded-lg font-medium text-left flex items-center justify-between ${isChildActive ? "bg-orange-50 text-brand" : "text-navy-800 hover:bg-slate-50"}`}
        >
          {link.label}
          <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </button>
        {expanded && (
          <div className="ml-4 mt-1 space-y-1">
            {link.children.map((child) => (
              <NavLink
                key={child.to}
                to={child.to}
                className={({ isActive }) =>
                  `block px-4 py-2.5 rounded-lg font-medium text-sm ${isActive ? "bg-orange-50 text-brand" : "text-navy-800 hover:bg-slate-50"}`
                }
              >
                {child.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={link.to}
      end={link.to === "/"}
      className={({ isActive }) =>
        `px-4 py-3 rounded-lg font-medium ${isActive ? "bg-orange-50 text-brand" : "text-navy-800 hover:bg-slate-50"}`
      }
    >
      {link.label}
    </NavLink>
  );
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <div className="sticky top-0 z-50">
      {/* Top info bar - hides on scroll */}
      <div className={`hidden md:block bg-navy-900 text-slate-200 text-xs overflow-hidden transition-all duration-300 ${scrolled ? "max-h-0 opacity-0" : "max-h-10 opacity-100"}`}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-9 flex items-center justify-between">
          <div className="flex items-center gap-5">
            <span className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-brand" /> {conference.datesShort}</span>
            <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-brand" /> {conference.venue}</span>
          </div>
          <div className="flex items-center gap-2">
            <a href="https://www.instagram.com/drsmumbai/" target="_blank" rel="noopener noreferrer" className="w-7 h-7 rounded-lg bg-white/10 hover:bg-brand flex items-center justify-center transition-colors" aria-label="Instagram">
              <Instagram className="w-3.5 h-3.5 text-white" />
            </a>
            <a href="https://www.facebook.com/DRSmumbai" target="_blank" rel="noopener noreferrer" className="w-7 h-7 rounded-lg bg-white/10 hover:bg-brand flex items-center justify-center transition-colors" aria-label="Facebook">
              <Facebook className="w-3.5 h-3.5 text-white" />
            </a>
            <a href="https://x.com/DRSmumbai" target="_blank" rel="noopener noreferrer" className="w-7 h-7 rounded-lg bg-white/10 hover:bg-brand flex items-center justify-center transition-colors" aria-label="X">
              <Twitter className="w-3.5 h-3.5 text-white" />
            </a>
          </div>
        </div>
      </div>

      <header className={`transition-all duration-300 ${scrolled ? "bg-white shadow-[0_8px_30px_-12px_rgba(12,28,60,0.25)]" : "bg-white/95 backdrop-blur"}`}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex items-center justify-between h-[72px]">
          <Logo onCard={false} />

          <nav className="hidden xl:flex items-center gap-0.5">
            {navLinks.map((l) => (
              <DesktopNavItem key={l.label} link={l} />
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-2 shrink-0">
            <a
              href={PHOTO_LINKS.web}
              onClick={(e) => { e.preventDefault(); openPhotoGallery(); }}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 border border-brand text-brand hover:bg-orange-50 font-semibold rounded-full px-4 py-2.5 text-sm whitespace-nowrap transition-colors"
            >
              <Camera className="w-4 h-4 shrink-0" /> Get Your Photos
            </a>
            <a
              href="https://www.certificate.rx-events.live/certificate/drs-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-shine inline-flex items-center gap-1.5 bg-brand hover:bg-brand-600 text-white font-semibold rounded-full px-4 py-2.5 text-sm whitespace-nowrap shadow-lg shadow-brand/25 transition-colors"
            >
              <Award className="w-4 h-4 shrink-0" /> Download Certificate
            </a>
          </div>

          <button onClick={() => setOpen(!open)} className="xl:hidden p-2 rounded-lg text-navy-800" aria-label="Menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div className="xl:hidden bg-white border-t border-slate-100 shadow-xl">
            <nav className="flex flex-col p-4 gap-1 max-w-7xl mx-auto">
              {navLinks.map((l) => (
                <MobileNavItem key={l.label} link={l} />
              ))}
              <a href={PHOTO_LINKS.web} onClick={(e) => { e.preventDefault(); openPhotoGallery(); }} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center justify-center gap-2 border border-brand text-brand font-semibold rounded-full py-3">
                <Camera className="w-4 h-4" /> Get Your Photos
              </a>
              <a href="https://www.certificate.rx-events.live/certificate/drs-2026" target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center justify-center gap-2 bg-brand text-white font-semibold rounded-full py-3">
                <Award className="w-4 h-4" /> Download Certificate
              </a>
            </nav>
          </div>
        )}
      </header>
    </div>
  );
};

export default Header;
